import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./ThreatExceptions.styles.css";
import { PageHeader } from "../../components/organisms/PageHeader";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Stack from "@mui/material/Stack";
import { DataTable } from "../../components/molecules/DataTable";
import { ActionsColumn } from "../../components/molecules/StyledMenu";
import {
  Drawer,
  FormControlLabel,
  styled,
  Switch,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography
} from "@mui/material";
import { Toast } from "../../components/atoms/Toast";
import LeavePageDialog from "../../components/organisms/LeavePageDialog/LeavePageDialog";
import { useCallbackPrompt } from "../../hooks/useCallbackPrompt";
import { isValidTagValue } from "../../utils/common";
import { GridColDef, GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import { Modal } from "../../components/atoms/Modal";
import { exceptionRule } from "../../api/emailTriage";
import WarningIcon from "@mui/icons-material/Warning";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ISearchMenuBarDropdown, SearchMenuBar } from "../../components/organisms/SearchMenuBar";
import {
  EXCEPTION_CATEGORY_NAMES,
  EXCEPTION_TYPE_NAMES,
  STATUS_CODE,
} from "../../constants/contants";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";
import ChangeWithZone from "../../hoc/ChangeWithZone";
import { useFormik } from "formik";
import { IExceptionEntry } from "./ThreatExceptions.props";
import * as yup from 'yup';
import { AddEditExceptionEntryModal } from "../../components/organisms/AddEditExceptionEntryModal";
import { Pagination } from "../../components/atoms/Pagination";
import { TagInputTypes } from "../../components/atoms/TagInput";
import { LoadingPage } from "../../components/organisms/LoadingPage";

const PERPAGES = 10;

const DEFAULT_TAB_FILTER_VALUES = {
  perPages: PERPAGES,
  page: 1,
  keyword: '',
  type: [],
  category: [],
  sortDir: 'desc',
  sortCol: 'created_at',
  dropdownFilters: { category: null, type: null },
};

const OVERRIDDEN_ICONS = {
  [EXCEPTION_CATEGORY_NAMES.undetermined]: <RemoveCircleIcon color="warning"/>,
  [EXCEPTION_CATEGORY_NAMES.clean]: <CheckCircleIcon/>,
  [EXCEPTION_CATEGORY_NAMES.threat]: <WarningIcon color="error"/>,
}

export const EXCEPTION_RULE_ACTIONS = {
  CREATE: 'create',
  LIST: 'list',
  UPDATE: 'update',
  UPDATE_STATUS: 'update_exception_rules_status',
  DELETE: 'delete',
}

export const OverriddenValue: React.FC<{ value: string }> = ({ value }) => (
  <Stack direction="row" alignItems="center" gap={1}>
    {OVERRIDDEN_ICONS[EXCEPTION_CATEGORY_NAMES[value]]}<strong>{EXCEPTION_CATEGORY_NAMES[value]}</strong>
  </Stack>
)

const GreenSwitch = styled(Switch)(() => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: "#008545",
    '&:hover': {
      backgroundColor: "#B3DAC7",
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: "#B3DAC7",
  },
}));

const DuplicateTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip className={'dupe-custom-tooltip'} {...props} classes={{ popper: className }}/>
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    alignItems: "center",
    maxHeight: "400px",
    maxWidth: "1000px",
    borderRadius: "4px",
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
    fontSize: '13px',
    fontWeight: '500',
    padding: '8px',
    marginLeft: '-22px',
  },
}));

type ExceptionsData = {
  id: string;
  status: "active" | "inactive";
  [key: string]: any;
};
type CheckMap = { [id: string]: boolean };
const toStatusStr = (b: boolean) => (b ? "active" : "inactive");
const getChangedExceptionUpdated = (
  exceptionsData: ExceptionsData[],
  check: CheckMap
): ExceptionsData[] => {
  const changed: ExceptionsData[] = [];
  for (const t of exceptionsData) {
    if (Object.prototype.hasOwnProperty.call(check, t.id)) {
      const newStatus = toStatusStr(check[t.id]);
      if (t.status !== newStatus) {
        changed.push({ id: t.id, status: newStatus });
      }
    }
  }
  return changed;
};

const ExceptionsList: React.FC = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const actionsOpen = Boolean(anchorEl);
  const [isShowLoading, setShowLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [exceptionsData, setExceptionsData] = useState([]);
  const [sortCol, setSortCol] = useState(DEFAULT_TAB_FILTER_VALUES.sortCol);
  const [sortDir, setSortDir] = useState(DEFAULT_TAB_FILTER_VALUES.sortDir);
  const [sortModel, setSortModel] = useState<GridSortModel>([{
    field: 'created_at',
    sort: 'desc',
  }]);
  const [isActionShow, setActionShow] = React.useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState<boolean>(false);
  const [toastValue, setToastValue] = useState({ type: '', message: '', isOpen: false, toastId: 0 });
  const [modalID, setModalID] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(DEFAULT_TAB_FILTER_VALUES.page);
  const [perPages, setPerPages] = useState(DEFAULT_TAB_FILTER_VALUES.perPages);
  const [startPoint, setStartPoint] = useState(0)

  const [endPoint, setEndPoint] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(showLeaveDialog)
  const [openDelete, setOpenDelete] = useState(false);
  const [currentActionSelection, setCurrentActionSelection] = useState('');
  const [selectedItems, setSelectedItems] = useState<any>({});
  const [filterSearchKeyword, setFilterSearchKeyword] = useState(DEFAULT_TAB_FILTER_VALUES.keyword);
  const [pendingFilterSearchKeyword, setPendingFilterSearchKeyword] = useState(filterSearchKeyword);
  const [addEntryModalOpen, setAddEntryModalOpen] = useState(false);
  const [entryValueTags, setEntryValueTags] = useState([] as string[]);
  const [checked, setChecked] = useState({});
  const [initChecked, setInitChecked] = useState({});
  const [tagInputError, setTagInputError] = useState(false);
  const [openDuplicate, setOpenDuplicate] = useState(false);
  const [duplicateEntries, setDuplicateEntries] = useState([] as string[]);
  const [openUnsavedConfirm, setOpenUnsavedConfirm] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [pendingNewEntryOpen, setPendingNewEntryOpen] = useState(false);
  const [pendingPage, setPendingPage] = useState(currentPage);
  const [pendingPerPage, setPendingPerPage] = useState(DEFAULT_TAB_FILTER_VALUES.perPages);
  const [filterResetPending, setFilterResetPending] = useState(false);
  const [pendingFilterDropdownOpen, setPendingFilterDropdownOpen] = useState(DEFAULT_TAB_FILTER_VALUES.dropdownFilters);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(DEFAULT_TAB_FILTER_VALUES.dropdownFilters);
  const { t } = useTranslation();

  const valuesInputRef = useRef(null);
  const pendingKeywordRef = useRef<any>(null);

  const getNameKey = (name: string, namesObj: object) => Object.keys(namesObj).find(key => namesObj[key] === name) || '';

  const localStorageUserData = JSON.parse(localStorage.getItem('userData') || '{}');
  const initialValues: IExceptionEntry = {
    id: '',
    type: TagInputTypes.URL,
    category: 'undetermined',
    value: '',
    notes: '',
    status: true,
  };
  const getValidationSchema = useMemo(() => {
    const baseSchema = yup.object({
      type: yup
        .string()
        .oneOf(Object.keys(EXCEPTION_TYPE_NAMES))
        .required(),

      category: yup
        .string()
        .oneOf(Object.keys(EXCEPTION_CATEGORY_NAMES))
        .required(),

      notes: yup
        .string()
        .trim()
        .required(t('validation.required', { field: 'Notes' })),
    });

    const valueSchema = yup.object({
      value: yup
        .string()
        .trim()
        .required(t('validation.required', { field: 'Value' }))
        .test('value-validation', function (value) {
          const { type } = this.parent;

          if (!isValidTagValue(type, value)) {
            return this.createError({
              message: t(`errors.threatExceptions.${type}`),
            });
          }

          return true;
        }),
    });

    return !currentActionSelection
      ? baseSchema
      : baseSchema.concat(valueSchema);
  }, [currentActionSelection]);

  const formikAddEditExceptionEntry = useFormik({
    initialValues,
    validationSchema: getValidationSchema,
    onSubmit: (values, { setFieldError }) => {
      const payload = {
        type: values.type,
        category: values.category,
        notes: values.notes,
        value: entryValueTags,
        created_by: localStorageUserData?.['fullName'] || '',
        created_by_email: localStorageUserData?.['email'] || '',
        status: toStatusStr(values.status),
      };

      if (currentActionSelection) {
        Object.assign(payload, { id: currentActionSelection, value: values.value });
      }

      if (entryValueTags.length || !!currentActionSelection) {
        setLoadingPage(true);
        exceptionRule({
          action: !!currentActionSelection ? EXCEPTION_RULE_ACTIONS.UPDATE : EXCEPTION_RULE_ACTIONS.CREATE,
          payload,
        }).then((res) => {
          if (res.status === STATUS_CODE.OK) {
            if (res.data.duplicateValues) {
              if (!!currentActionSelection) {
                setFieldError('value', t('errors.threatExceptions.duplicateEdit'));
              } else {
                setOpenDuplicate(true);
                setDuplicateEntries(res.data.duplicateValues);
              }
            } else {
              setModalID(modalID + 1);
              setToastValue({ type: 'success', message: t('messages.genericSaved'), isOpen: true, toastId: modalID });
              handleCloseModal();
              getExceptionsTable();
              setTagInputError(false);
            }
          }
        }).catch(err => {
          console.error(err)
          setShowLoading(false);
          setModalID(modalID + 1);
          setToastValue({ type: 'error', message: t('messages.genericError'), isOpen: true, toastId: modalID });
        }).finally(() => {
          setLoadingPage(false);
        });
      }
    },
    validateOnChange: false,
    validateOnBlur: true,
  });

  const dropdownFilters: ISearchMenuBarDropdown[] = [
    {
      label: t('fields.threatExceptions.type'),
      arrList: [
        {
          id: 0,
          content: EXCEPTION_TYPE_NAMES.url,
        },
        {
          id: 6,
          content: EXCEPTION_TYPE_NAMES.sender_domain,
        },
        {
          id: 1,
          content: EXCEPTION_TYPE_NAMES.domain,
        },
        {
          id: 2,
          content: EXCEPTION_TYPE_NAMES.ip,
        },
        {
          id: 3,
          content: EXCEPTION_TYPE_NAMES.dns,
        },
        {
          id: 4,
          content: EXCEPTION_TYPE_NAMES.email,
        },
        {
          id: 5,
          content: EXCEPTION_TYPE_NAMES.file_hash,
        }
      ],
      type: 'type',
      width: 143,
    },
    {
      label: t('fields.threatExceptions.category'),
      arrList: [
        {
          id: 1,
          content: EXCEPTION_CATEGORY_NAMES.undetermined,
          customLabel: <OverriddenValue
            value={getNameKey(EXCEPTION_CATEGORY_NAMES.undetermined, EXCEPTION_CATEGORY_NAMES)}/>,
        },
        {
          id: 2,
          content: EXCEPTION_CATEGORY_NAMES.clean,
          customLabel: <OverriddenValue value={getNameKey(EXCEPTION_CATEGORY_NAMES.clean, EXCEPTION_CATEGORY_NAMES)}/>,
        },
        {
          id: 3,
          content: EXCEPTION_CATEGORY_NAMES.threat,
          customLabel: <OverriddenValue value={getNameKey(EXCEPTION_CATEGORY_NAMES.threat, EXCEPTION_CATEGORY_NAMES)}/>,
        },
      ],
      type: 'category',
      width: 176,
    },
  ];

  const processExceptionRulesResponse = (res: any) => {
    if (res && res.status === STATUS_CODE.OK) {
      const { list, endItemsPerPage, maxPage, startItemsPerPage, totalItems } = res.data;
      setExceptionsData(list || []);
      setTotalPages(maxPage);
      setTotalRows(totalItems);
      setStartPoint(startItemsPerPage);
      setEndPoint(endItemsPerPage);
      setShowLoading(false);
      let newChecked = { ...checked };
      list.forEach((exception: any) => {
        newChecked = { ...newChecked, [exception.id]: (exception.status === 'active') }
      });
      setInitChecked(newChecked);
      setChecked(newChecked);
    }
  }

  const getExceptionsTable = useCallback(() => {
    setShowLoading(true);
    exceptionRule({
      action: EXCEPTION_RULE_ACTIONS.LIST,
      payload: {
        filters: {
          type: selectedItems.type?.map((t = '') => getNameKey(t, EXCEPTION_TYPE_NAMES)) || [],
          category: selectedItems.category?.map((t = '') => getNameKey(t, EXCEPTION_CATEGORY_NAMES)) || []
        },
        keyword: filterSearchKeyword,
        sort_col: sortCol,
        sort_dir: sortDir,
        page: currentPage,
        per_page: perPages,
      },
    }).then((res: any) => {
      processExceptionRulesResponse(res);
    }).catch(err => {
      console.error("err: ", err);
      setShowLoading(false);
    });
  }, [
    selectedItems,
    filterSearchKeyword,
    sortCol,
    sortDir,
    currentPage,
    perPages,
  ]);

  useEffect(() => {
    getExceptionsTable();
  }, [getExceptionsTable]);

  useEffect(() => {
    setEntryValueTags([]);
  }, [formikAddEditExceptionEntry.values.type]);

  useEffect(() => {
    if (!addEntryModalOpen) {
      formikAddEditExceptionEntry.resetForm();
      setEntryValueTags([]);
      setTagInputError(false);
      setCurrentActionSelection('');
    } else {
      setAnchorEl(null);
    }
  }, [addEntryModalOpen]);

  useEffect(() => {
    if (!isActionShow && filterResetPending) {
      resetFilters();
    }
  }, [isActionShow, filterResetPending]);

  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      getExceptionsTable();
    }
  }, [filterSearchKeyword]);

  const resetFilters = () => {
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setFilterResetPending(true);
    } else {
      const { keyword, perPages, type, category, page, sortCol, sortDir } = DEFAULT_TAB_FILTER_VALUES;
      setFilterSearchKeyword(keyword);
      setPendingFilterSearchKeyword(keyword);
      setPerPages(perPages);
      setSelectedItems({ type, category });
      setCurrentPage(page);
      setSortCol(sortCol);
      setSortDir(sortDir);
      setSortModel([{
        sort: sortDir as GridSortDirection,
        field: sortCol,
      }])
      setFilterResetPending(false);
    }
  }

  const handlePerPageRowsChange = (e: any) => {
    const value = parseInt(e.target.value);
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setPendingPerPage(value);
    } else {
      setPerPages(value);
      if (currentPage > 1) {
        setCurrentPage(1);
      }
    }
  };

  const handlePageChange = (page: number) => {
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setPendingPage(page);
    } else {
      setCurrentPage(page);
    }
  }

  const paginationComponent = () => {
    return <Pagination
      itemsPerPage={perPages}
      currentPage={currentPage}
      startPoint={startPoint}
      endPoint={endPoint}
      maxPage={totalPages}
      totalItems={totalRows}
      handlePerRowsChange={handlePerPageRowsChange}
      handlePageChange={handlePageChange}
    />
  }

  const changeStatusRule = (status: any, row: any) => {
    const isChecked = status.target.checked
    setChecked({
      ...checked,
      [row.id]: isChecked
    })
    setActionShow(true);
    setShowLeaveDialog(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'value', headerName: t('fields.threatExceptions.value'), width: 500, sortable: true,
      sortingOrder: ['asc', 'desc'],
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
      renderCell: (params) => (
        <Tooltip
          title={params.value}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                alignItems: "center",
                maxWidth: "680px",
                borderRadius: "4px",
                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
                fontSize: '13px',
                fontWeight: '500',
                padding: '8px',
                marginLeft: '54px',
                textOverflow: 'initial',
              },
            },
          }}
        >
        <span className="MuiDataGrid-cellContent">
          {params.value}
        </span>
        </Tooltip>
      ),
    },
    {
      field: 'type', headerName: t('fields.threatExceptions.type'), flex: 1, maxWidth: 172, sortable: true,
      sortingOrder: ['asc', 'desc'],
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
      renderCell: ({ value }) => (<>{EXCEPTION_TYPE_NAMES[value]}</>)
    },
    {
      field: 'category', headerName: t('fields.threatExceptions.category'), flex: 1, maxWidth: 200, sortable: true,
      sortingOrder: ['asc', 'desc'],
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
      renderCell: ({ value }) => <OverriddenValue value={value}/>
    },
    {
      field: 'created_at',
      headerName: t('fields.threatExceptions.createdAt'),
      minWidth: 180,
      maxWidth: 180,
      sortable: true,
      sortingOrder: ['asc', 'desc'],
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
      renderCell: ({ value }) => moment(value * 1000).format('DD/MM/YYYY HH:mm A'),
    },
    {
      field: 'created_by',
      headerName: t('fields.threatExceptions.createdBy'),
      minWidth: 182,
      maxWidth: 182,
      sortable: true,
      sortingOrder: ['asc', 'desc'],
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
      renderCell: ({row}) => (
        <Stack gap={row.created_by_email ? 0.5 : 0}>
          <Typography variant="body2" fontWeight={700}>{row.created_by}</Typography>
          <Tooltip placement={"top"} title={row.created_by_email}>
            <Typography className={'MuiDataGrid-cellContent'} variant="body2" whiteSpace="nowrap"
                        maxWidth={165}>{row.created_by_email}</Typography>
          </Tooltip>
        </Stack>
      )
    },
    {
      field: 'notes', headerName: t('fields.threatExceptions.notes'), minWidth: 220, maxWidth: 220, sortable: false,
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
    },
    {
      field: 'status', headerName: t('fields.templates.status'), width: 140, sortable: true,
      sortingOrder: ['asc', 'desc'],
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
      renderCell: (row: any) => {
        return <FormControlLabel className={`status-col ${checked[row.id] ? 'active' : 'inactive'}`} control={
          <GreenSwitch checked={checked[row.id]} onChange={(status: any) => changeStatusRule(status, row)}/>
        } label={checked[row.id] ? 'Active' : 'Inactive'}/>
      }
    },
  ];

  const onSort = (model: any) => {
    setSortDir(model?.length !== 0 ? model[0].sort : 'asc');
    setSortCol(model?.length !== 0 ? model[0].field : 'type');
    setSortModel(model);
    setActionShow(false)
    setShowLeaveDialog(false)
  }

  const handleDiscard = () => {
    setActionShow(false)
    setShowLeaveDialog(false);
    setChecked(initChecked);
  }

  const handleSaveChange = () => {
    setShowLoading(true)
    const updatedItems = getChangedExceptionUpdated(exceptionsData, checked);
    exceptionRule({
      action: EXCEPTION_RULE_ACTIONS.UPDATE_STATUS,
      payload: { exception_rules: updatedItems }
    }).then((response: any) => {
      if (response.status === STATUS_CODE.OK) {
        setModalID(modalID + 1)
        setToastValue({ type: 'success', message: t('messages.genericSaved'), isOpen: true, toastId: modalID })
        getExceptionsTable()
        setActionShow(false)
        setShowLoading(false)
        setShowLeaveDialog(false);
      }
    }).catch((err: any) => {
      console.error("err: ", err);
      setShowLoading(false);
      setModalID(modalID + 1);
      setToastValue({ type: 'error', message: t('messages.genericError'), isOpen: true, toastId: modalID });
    });
  }

  const saveChangeUI = (
    <Box className={'save-change-content'}
         sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px 0' }}>
      <div className={'btn discard-btn'}>
        <Button variant="outlined" onClick={handleDiscard}>{t('actions.discard')}</Button>
      </div>
      <div className={'btn save-btn'}>
        <Button variant="contained" onClick={handleSaveChange}>{t('actions.saveChanges')}</Button>
      </div>
    </Box>
  )

  const showActionsDropdown = (event: React.MouseEvent<HTMLElement>) => {
    const exceptionId: any = event.currentTarget.getAttribute('data-rowid');
    setCurrentActionSelection(exceptionId);
    setAnchorEl(event.currentTarget);
  };

  const hideActionsDropdown = () => {
    const gridRows = document.querySelectorAll('.MuiDataGrid-row');
    if (gridRows) {
      for (let i = 0; i < gridRows.length; i++) {
        gridRows[i].classList.remove('focusing');
      }
    }
    setAnchorEl(null);
  };

  const handleActionDelete = (params?: any, page?: string) => {
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setPendingDelete(true);
    } else {
      setOpenDelete(true);
    }
  };

  const handleActionEdit = () => {
    const {
      id,
      value,
      type,
      category,
      notes,
      status,
    } = exceptionsData[exceptionsData.findIndex(exception => exception['id'] === currentActionSelection)] || {};
    formikAddEditExceptionEntry.setValues({
      id,
      value,
      type,
      category,
      notes,
      status: status === 'active',
    }).then(() => {
      if (isActionShow) {
        setOpenUnsavedConfirm(true);
        setPendingEdit(true);
      } else {
        setAddEntryModalOpen(true);
      }
    });
  };

  const handleAddNew = () => {
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setPendingNewEntryOpen(true);
    } else {
      setAddEntryModalOpen(true);
    }
  }

  const handleCloseModal = () => {
    setOpenDelete(false);
    setAddEntryModalOpen(false);
    setOpenDuplicate(false);
    hideActionsDropdown();
    formikAddEditExceptionEntry.setValues(initialValues);
  }

  const handleDeleteEntry = () => {
    setShowLoading(true)
    exceptionRule({
      action: EXCEPTION_RULE_ACTIONS.DELETE,
      payload: { exception_rule_id: currentActionSelection }
    }).then((res: any) => {
      setModalID(modalID + 1);
      setToastValue({
        type: 'success',
        message: t('messages.threatExceptions.deleteSuccess'),
        isOpen: true,
        toastId: modalID
      })
      if (currentPage === 1) {
        getExceptionsTable();
      } else {
        setCurrentPage(1);
      }
      setCurrentActionSelection('');
    }).catch((err: any) => {
      console.error("err: ", err);
      setShowLoading(false);
      setModalID(modalID + 1);
      setToastValue({ type: 'error', message: t('messages.genericError'), isOpen: true, toastId: modalID });
    });
  }

  const handleFilter = (item: any, type: any) => {
    setSelectedItems((prev: any) => {
      return ({
        ...prev,
        [type]: item,
      });
    });
    setCurrentPage(1);
  };

  const onSearchInboxByKeywords = (event: any) => {
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
    } else {
      if (event.keyCode === 13) {
	setFilterSearchKeyword(pendingFilterSearchKeyword)
      }
    }
  };

  const handleClearFilter = (type: any) => {
    setSelectedItems((prev: any) => {
      return ({
        ...prev,
        [type]: [],
      })
    });
  };

  const tagsChangeHandler = (_ = '', newTags: string[]) => {
    setEntryValueTags(newTags);
  }

  const duplicateModalAgreeHandler = () => {
    const newTags = entryValueTags.filter(tag => !duplicateEntries.includes(tag));
    setEntryValueTags(newTags);
    setOpenDuplicate(false);
  };

  const dropdownFilterOpenHandler = (value: any) => {
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setPendingFilterDropdownOpen(value);
    } else {
      setFilterDropdownOpen(value);
    }
  };

  const resetPendingStates = () => {
    setPendingDelete(false);
    setPendingEdit(false);
    setPendingNewEntryOpen(false);
    setPendingPage(currentPage);
    setPendingPerPage(perPages);
    setPendingFilterDropdownOpen(DEFAULT_TAB_FILTER_VALUES.dropdownFilters);
  };

  useEffect(() => {
    const shouldResetPending = addEntryModalOpen || openDelete || currentPage === pendingPage || perPages === pendingPerPage || JSON.stringify(filterDropdownOpen) !== JSON.stringify(pendingFilterDropdownOpen);
    if (shouldResetPending) {
      resetPendingStates();
    }
  }, [addEntryModalOpen, openDelete, currentPage, perPages, filterDropdownOpen]);

  const handleContinueWhenUnsaved = () => {
    setAddEntryModalOpen(pendingNewEntryOpen || pendingEdit);
    setFilterDropdownOpen(pendingFilterDropdownOpen);
    setOpenDelete(pendingDelete);
    pendingKeywordRef?.current?.focus();
    if (pendingPage !== currentPage) {
      setCurrentPage(pendingPage);
    }
    if (pendingPerPage !== perPages) {
      setPerPages(pendingPerPage);
    }
    setOpenUnsavedConfirm(false);
    setActionShow(false);
    setShowLeaveDialog(false);
    setChecked(initChecked);
  };

  const handleCancelWhenUnsaved = () => {
    resetPendingStates();
    setAddEntryModalOpen(false);
    setCurrentActionSelection('');
    setOpenUnsavedConfirm(false);
    setAnchorEl(null);
  };

  const handleKeywordFocus = (event?: React.FocusEvent<HTMLInputElement>) => {
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      event?.preventDefault();
    }
  }

  return (
    <div className="threat-exceptions-page page-content-wrapper">
      <PageHeader
        pageTitle={t('pageTitles.threatExceptions.list')}
        tooltipStyle={{ minWidth: '650px' }}
        breadCrumbData={[
          {
            name: "Settings",
            href: "/settings",
          },
          {
            name: t('pageTitles.threatExceptions.list')
          }
        ]}
        children={
          <Button
            className="new-entry-btn"
            data-testid="new-entry-btn"
            onClick={() => handleAddNew()}
            variant="contained"
            size="large"
          >
            <AddIcon
              style={{ fontSize: '20px' }}/>
            <span style={{ paddingLeft: '8px' }}>{t('actions.threatExceptions.new')}</span>
          </Button>
        }
      />
      <Stack className="page-content-body">
        <div className="threat-exceptions-content">
          <div id="search-menu-container"><SearchMenuBar
            selectedItems={selectedItems}
            resetFilter={resetFilters}
            handleEnterSearch={onSearchInboxByKeywords}
            onFilter={handleFilter}
            searchInputValue={pendingFilterSearchKeyword}
            setSearchInputValue={setPendingFilterSearchKeyword}
            onClearFilter={handleClearFilter}
            dropdownFilters={dropdownFilters}
            filtersOpen={filterDropdownOpen}
            setFiltersOpen={dropdownFilterOpenHandler}
            handleKeywordFocus={handleKeywordFocus}
            searchFilterRef={pendingKeywordRef}
          /></div>
          <div className="exception-datatable">
            <DataTable
              columns={columns}
              data={exceptionsData}
              actionsColumn={<ActionsColumn
                open={actionsOpen}
                anchorEl={anchorEl}
                hideActionsDropdown={hideActionsDropdown}
                menuData={exceptionsData}
                showActionsDropdown={showActionsDropdown}
                handleDelete={handleActionDelete}
                handleEdit={handleActionEdit}
                isException
              />}
              pagination
              CustomPagination={paginationComponent}
              loadingTable={isShowLoading}
              onSortModelChange={(model: any) => onSort(model)}
              sortModel={sortModel}
            />
          </div>
        </div>
      </Stack>
      <Drawer
        variant="persistent"
        anchor="bottom"
        open={isActionShow}
        onClose={() => setActionShow(false)}
        className={'save-change-container'}
        children={saveChangeUI}
      ></Drawer>
      <Toast
        isOpen={toastValue.isOpen}
        type={toastValue.type}
        message={toastValue.message}
        toastId={toastValue.toastId}
        autoHideDuration={2000}
      />
      <LeavePageDialog
        showDialog={showPrompt}
        confirmNavigation={confirmNavigation}
        cancelNavigation={cancelNavigation}
      />
      <Modal
        idModal="modal-delete-exception"
        openDialog={openDelete}
        variant="delete"
        title="Delete entry"
        content="Are you sure to delete this entry?"
        actionAgree={/* istanbul ignore next */() => {
          handleDeleteEntry()
          /* istanbul ignore next */
          handleCloseModal()
        }}
        actionCancel={handleCloseModal}
        textAgree="Delete"
        onClose={handleCloseModal}
      />
      <Modal
        idModal="modal-add-edit-exception"
        title={t(`pageTitles.threatExceptions.${!!currentActionSelection ? 'edit' : 'new'}Entry`)}
        openDialog={addEntryModalOpen}
        actionAgree={() => {
          setTagInputError(!entryValueTags.length);
          formikAddEditExceptionEntry.handleSubmit();
        }}
        actionCancel={handleCloseModal}
        onClose={handleCloseModal}
        textAgree={t('actions.save')}
        textCancel={t('actions.cancel')}
      >
        <AddEditExceptionEntryModal
          formData={formikAddEditExceptionEntry}
          entryValueTags={entryValueTags}
          tagsChangeHandler={tagsChangeHandler}
          tagRef={valuesInputRef}
          isEdit={!!currentActionSelection}
          tagInputError={tagInputError}
          setTagInputError={setTagInputError}
        />
      </Modal>
      <Modal
        idModal="modal-duplicate-exception"
        title={t(`pageTitles.threatExceptions.duplicateEntry`)}
        openDialog={openDuplicate}
        variant="delete"
        actionAgree={duplicateModalAgreeHandler}
        actionCancel={() => {
          setOpenDuplicate(false)
        }}
        onClose={() => {
          setOpenDuplicate(false)
        }}
        textAgree={t('actions.threatExceptions.removeDupes')}
        textCancel={t('actions.cancel')}
      >
        <Stack>
          <Typography>{t('errors.threatExceptions.duplicate')}</Typography>
          <ul id="dupe-values-list">
            {duplicateEntries.map((entry, index) => (
              <li>
                <DuplicateTooltip key={`dupe-${index}`} title={entry} placement="top">
                  <Typography>{entry}</Typography>
                </DuplicateTooltip>
              </li>
            ))}
          </ul>
        </Stack>
      </Modal>
      <Modal
        idModal="modal-unsaved-confirmation"
        openDialog={openUnsavedConfirm}
        variant="warning"
        title={t('pageTitles.threatExceptions.unsavedChanges')}
        content={t('messages.threatExceptions.unsavedChanges')}
        actionAgree={handleContinueWhenUnsaved}
        actionCancel={handleCancelWhenUnsaved}
        textAgree={t('actions.threatExceptions.discardChanges')}
        textCancel={t('actions.cancel')}
        onClose={handleCancelWhenUnsaved}
      />
      <LoadingPage open={loadingPage}/>
    </div>
  );
};

export const ThreatExceptions = ChangeWithZone(ExceptionsList);
